"use client";

import { ChevronDown, Package } from "@/components/icons";
import { Image } from "@/components/ui/image";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useListingBrowseContext } from "../context/listing-browse-context";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import {
  PRODUCT_TYPES,
  PRODUCT_TYPE_CONDITIONS,
  getProductTypeBySlug,
  isProductType,
  type ProductType,
} from "@/lib/generated/product-types";
import { Button } from "@/components/ui/button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { SET_CHIP_CLASS } from "./filter-chip";

export const QUANTITY_PRESETS = [1, 24, 48, 96, 200] as const;
export const PRODUCT_TYPE_OPTIONS = [...PRODUCT_TYPES].sort(
  (left, right) => left.displayOrder - right.displayOrder,
);

function FilterTrigger({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <Button
      type="button"
      variant="outline"
      size="compact"
      className={cn("rounded-sm", className)}
      {...props}
    >
      {children}
    </Button>
  );
}

function FilterOption({
  isSelected,
  onSelect,
  children,
}: {
  isSelected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="compact"
      onClick={onSelect}
      className={cn(
        "text-left w-full justify-start rounded-sm text-sm",
        isSelected && "hover:bg-action-tint bg-action-tint text-action",
      )}
    >
      {children}
    </Button>
  );
}

export function conditionsKeptFor(
  productTypeSlug: string,
  conditions: readonly string[],
): string[] {
  const offered = isProductType(productTypeSlug)
    ? (PRODUCT_TYPE_CONDITIONS[productTypeSlug] ?? [])
    : [];
  const offeredValues = new Set(offered.map((option) => option.value));
  return conditions.filter((entry) => offeredValues.has(entry));
}

export function useSelectProductType() {
  const { setProductType, conditions, setConditions } = useListingBrowseContext();
  return (productTypeSlug: string) => {
    setProductType(productTypeSlug);
    const kept = conditionsKeptFor(productTypeSlug, conditions);
    if (kept.length !== conditions.length) setConditions(kept);
  };
}

function ProductTypeTrigger({
  iconUrl,
  label,
  isSet,
}: {
  iconUrl: string | null | undefined;
  label: string;
  isSet: boolean;
}) {
  return (
    <PopoverTrigger asChild>
      <FilterTrigger className={cn(isSet && SET_CHIP_CLASS)}>
        {iconUrl ? (
          <Image src={iconUrl} alt="" width={16} height={16} className="size-4 object-contain" />
        ) : (
          <Package className="text-muted-foreground" />
        )}
        <span className="text-muted-foreground">Product:</span>
        <span className={cn("text-foreground", isSet && "font-semibold")}>{label}</span>
        <ChevronDown className="text-muted-foreground" />
      </FilterTrigger>
    </PopoverTrigger>
  );
}

export function ProductTypeFilter() {
  const { productType, setProductType, lockedProductType } = useListingBrowseContext();
  const selectProductType = useSelectProductType();
  if (lockedProductType) return null;

  const selectedType = productType ? getProductTypeBySlug(productType) : null;
  const displayLabel = selectedType?.pluralDisplayName ?? "All";

  return (
    <Popover>
      <ProductTypeTrigger
        iconUrl={selectedType?.iconUrl}
        label={displayLabel}
        isSet={selectedType !== null}
      />
      <PopoverContent
        align="start"
        className="w-[220px] p-1 rounded-sm border-border"
      >
        <ScrollArea className="max-h-[320px]">
          <FilterOption
            isSelected={productType === null}
            onSelect={() => setProductType(null)}
          >
            <Package className="text-muted-foreground" />
            <span>All products</span>
          </FilterOption>
          {PRODUCT_TYPE_OPTIONS.map((type) => (
            <ProductTypeOption
              key={type.slug}
              type={type}
              isSelected={productType === type.slug}
              onSelect={() => selectProductType(type.slug)}
            />
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function ProductTypeOption({
  type,
  isSelected,
  onSelect,
}: {
  type: ProductType;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <FilterOption isSelected={isSelected} onSelect={onSelect}>
      {type.iconUrl ? (
        <Image
          src={type.iconUrl}
          alt=""
          width={16}
          height={16}
          className="size-4 object-contain"
        />
      ) : (
        <Package className="text-muted-foreground" />
      )}
      <span>{type.pluralDisplayName}</span>
    </FilterOption>
  );
}

export function QuantityFields() {
  const heroMap = useHeroMapContext();
  const quantity = heroMap.quantity ?? 1;
  const isPreset = QUANTITY_PRESETS.some((preset) => preset === quantity);

  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">Quantity needed</div>
      <SegmentedControl
        size="compact"
        aria-label="Quantity needed"
        className="mb-2 tabular-nums"
        value={String(quantity)}
        onValueChange={(next) => heroMap.setQuantity(Number(next))}
        options={QUANTITY_PRESETS.map((preset) => ({
          value: String(preset),
          label: String(preset),
        }))}
      />
      <input
        type="number"
        min={1}
        value={isPreset ? "" : quantity}
        onChange={(e) =>
          heroMap.setQuantity(Math.max(1, parseInt(e.target.value) || 1))
        }
        className="w-full h-8 px-2 text-sm tabular-nums border border-border rounded-sm bg-card text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]"
        aria-label="Custom quantity"
        placeholder="Custom quantity"
      />
    </div>
  );
}
